import React, { useState, useEffect, useRef } from "react";

// Modules
import { useSelector } from "react-redux";

// App
import { getOnlineNode } from "../../../../core/getNode";
import PresentationFilters from "../filters";
import NoData from "../../../../partials/noData";
import Presentation from "../presentation/view/presentation";
import SlideThumbnail from "../slide/view/thumbnail";
import Pagination from "../../../../partials/pagination";
import { jsonParser } from "../../../../utils/helpers";
import { getFavoritesByContentType } from "../../../../utils/helpers";

// UI components
import { Container, Row, Col } from "react-bootstrap";
import {
  AppBar,
  Dialog,
  DialogContent,
  Stack,
  Toolbar,
  Typography,
  Button,
  DialogActions,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CompanyappLoader from "../../../../partials/companyappLoader";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import useDeckSuspended from "../../../../partials/useDeckSuspended";

export default function AddTranslatedSlide(props) {
  // Full-screen dialog: the editor's Reveal deck is completely hidden behind
  // it but stays mounted, and re-styling it on every resize tick is what froze
  // the window. Suspending it while the translation picker is open costs nothing
  // visually. See useDeckSuspended / `body.deck-suspended`.
  useDeckSuspended(props.fromTranslateOpen);

  const [isLoading, setLoading] = useState(true);
  const [overlayLoader, setOverlayLoader] = useState(false);
  const [isError, setError] = useState(false);
  const [isPaginating, setPaging] = useState(false);
  const [isInserting, setIsInserting] = useState(false);
  const slidesContainer = useRef();
  const requestIdRef = useRef(0);

  const [language, setLanguage] = useState("");

  /**
   * Pagination
   */
  const pageSize = 12;
  // eslint-disable-next-line
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  /**
   * Data
   */
  const [presentations, setPresentations] = useState([]);
  const [selectedPresentationId, setSelectedPresentationId] = useState(null);
  const [selectedPresentation, setSelectedPresentation] = useState(null);
  const [selectedSlides, setSelectedSlides] = useState([]);
  const [presentationJson, setPresentationJson] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  /**
   * Filters Data
   */
  // const [title, setTitle] = useState('');
  // const [selectedCategory, setSelectedCategory] = useState(0);
  // const [author, setAuthor] = useState('');
  // const [selectedLibrary, setSelectedLibrary] = useState(0);
  // const [selectedStatus, setSelectedStatus] = useState(0);
  // const [selectedLanguage, setSelectedLanguage] = useState(0);
  // const [selectedTheme, setSelectedTheme] = useState(0);
  // const [selectedIDSita, setSelectedIDSita] = useState('');

  const [filters, setFilters] = useState({
    title: "",
    language: 0,
    category: 0,
    author: "",
    idSita: "",
    status: 0,
    library: 0,
  });

  const [searchString, setSearchString] = useState("");

  const favorites = useSelector((state) => state).favoritesReducer;
  const contentType = "presentation-item";
  const favStrings = getFavoritesByContentType(favorites, contentType, true);

  // Fetch content when filters or type changes (reset presentations)
  useEffect(() => {
    setError(false);
    setOverlayLoader(false);
    setLoading(true);
    setPresentations([]); // Reset presentations when filters change
    getContent(true);
    // eslint-disable-next-line
  }, [props.type, filters]);

  // Handle pagination separately (don't reset presentations)
  useEffect(() => {
    if (currentPage > 1) {
      setPaging(true);
      getContent(false);
    }
    // eslint-disable-next-line
  }, [currentPage]);

  // Handle activeIndex (favorites tab)
  useEffect(() => {
    if (activeIndex === 1 && favStrings === "") {
      setLoading(false);
      setPresentations([]);
      setTotalItems(0);
    } else {
      setLoading(true);
      setPresentations([]);
      getContent(true);
    }
    // eslint-disable-next-line
  }, [activeIndex]);

  // Handle favorites changes
  useEffect(() => {
    if (activeIndex === 1) {
      if (favStrings !== "") {
        setLoading(true);
        getContent(true);
      } else {
        setPresentations([]);
        setLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [favStrings]);

  useEffect(() => {
    if (selectedPresentationId) {
      setOverlayLoader(true);
      setSelectedPresentation(null);
      setSelectedSlides([]);
      setPresentationJson({});
      loadPresentation();
    }
    // eslint-disable-next-line
  }, [selectedPresentationId]);

  const getContent = (resetPresentations = false) => {
    // Increment request ID to track latest request
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    let path = `api/user_presentations?items_per_page=${pageSize}&page=${
      currentPage - 1
    }&presentation_status=${[
      "published",
      "modified",
    ]}&all_slides=true&live=true&exclude=${
      props.presentation.id
    }&simplified=true`;

    if (filters.category && filters.category !== 0) {
      path = `${path}&category=${filters.category}`;
    }

    if (filters.title && filters.title !== "") {
      path = `${path}&id_or_title=${encodeURIComponent(filters.title)}`;
    }

    if (filters.author && filters.author !== "") {
      if (typeof filters.author === "string") {
        path = `${path}&author_name=${filters.author}`;
      } else {
        if (filters.author.value) {
          path = `${path}&author_name=${filters.author.value}`;
        }
      }
    }

    if (filters.language && filters.language !== 0) {
      path = `${path}&language=${filters.language}`;
    }

    if (filters.library && filters.library !== 0) {
      path = `${path}&library=${filters.library}`;
    }

    if (filters.theme && filters.theme !== 0) {
      path = `${path}&theme=${filters.theme}`;
    }

    if (filters.idSita && filters.idSita !== "") {
      path = `${path}&id_or_sita=${filters.idSita}`;
    }

    if (filters.reference && filters.reference !== "") {
      path = `${path}&reference=${filters.reference}`;
    }

    // Only use lastId for pagination, not when resetting
    if (!resetPresentations && presentations.length > 0) {
      const lastElement = presentations[presentations.length - 1];

      if (lastElement && lastElement.id) {
        path = `${path}&lastId=${lastElement.id}`;
      }
    }

    if (activeIndex === 1 && favStrings !== "") {
      path = `${path}&id=${favStrings}`;
    }

    getOnlineNode(path)
      .then((response) => {
        // Only update state if this is still the latest request
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        setPresentations(response.data.rows);
        setTotalItems(response.data.pager.total_items);
        setPaging(false);
        setLoading(false);
      })
      .catch((_error) => {
        // Only update state if this is still the latest request
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        setLoading(false);
        setError(true);
      });
  };

  const loadPresentation = () => {
    let path = `api/user_presentations?presentation_status=${[
      "published",
      "modified",
    ]}&all_slides=true&live=true&id=${selectedPresentationId}`;

    getOnlineNode(path)
      .then((response) => {
        setOverlayLoader(false);

        if (response.data.rows.length > 0) {
          setSelectedSlides([]);
          setSelectedPresentation(response.data.rows[0]);
          setPresentationJson({ ...jsonParser(response.data.rows[0].json) });

          slidesContainer?.current?.scrollIntoView({ behavior: "smooth" });
        } else {
          // Presentation not found, clear selection but don't close dialog
          setSelectedPresentationId(null);
          setSelectedPresentation(null);
          setError(true);
        }
      })
      .catch((_error) => {
        setOverlayLoader(false);
        setSelectedPresentationId(null);
        setError(true);
      });
  };

  const onClose = () => {
    setSelectedPresentationId(null);
    setSelectedPresentation(null);
    setSelectedSlides([]);
    props.onLayoutClose();
  };

  const onSlideToggle = (slide) => {
    let newSelectedSlides = [...selectedSlides];
    if (!newSelectedSlides.includes(slide.id)) {
      newSelectedSlides.push(slide.id);
    } else {
      newSelectedSlides.splice(newSelectedSlides.indexOf(slide.id), 1);
    }

    setSelectedSlides(newSelectedSlides);
  };

  const selectAllSlides = () => {
    const allSlides = selectedPresentation.slides
      .map((slide) => {
        if (props.type === "copy") {
          return slide.id;
        }
        if (slide.prevent_duplicate !== "1" && slide.published) {
          return slide.id;
        }
        return null;
      })
      .filter(Boolean);

    setSelectedSlides(allSlides);
  };

  const processNotes = () => {
    let notesObj = {};
    if (selectedSlides.length > 0) {
      selectedSlides.forEach((e) => {
        if (presentationJson?.notes?.hasOwnProperty(e)) {
          notesObj[e] = presentationJson.notes[e];
        }
      });
    }
    if (Object.keys(notesObj).length > 0) {
      return notesObj;
    } else return null;
  };

  const processAudio = () => {
    let slideAudio = {};
    if (selectedSlides.length > 0) {
      selectedSlides.forEach((e) => {
        if (presentationJson?.slideAudio?.hasOwnProperty(e)) {
          slideAudio[e] = presentationJson.slideAudio[e];
        }
      });
    }
    if (Object.keys(slideAudio).length > 0) {
      return slideAudio;
    } else return null;
  };

  const checkStringMatch = (str, slide) => {
    if (!str) return false;

    // if numberic, check ID
    if (/^\d+$/.test(str)) {
      return String(slide.id).includes(str);
    }

    return slide.title.toLowerCase().includes(str.toLowerCase());
  };

  return (
    <Dialog
      fullScreen={true}
      open={props.fromTranslateOpen}
      onClose={props.onLayoutClose}
      className="presentation-library-modal"
    >
      <AppBar
        sx={{
          position: "relative",
          backgroundColor: "primary.light",
          color: "solid.dark",
        }}
      >
        <DialogContent sx={{ paddingTop: 1, paddingBottom: 1 }}>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Row className="w-100">
              <Col>
                {/* <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}> */}
                <Typography variant="h3" owner="admin" color="adminText.dark">
                  {props.title}
                </Typography>
              </Col>
              <Col className="d-flex justify-content-end align-items-center">
                <Row>
                  <Col className="d-flex justify-content-end">
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outlined"
                        owner="admin"
                        dialogbtn="true"
                        dialogvariant="cancel"
                        onClick={() => {
                          onClose();
                        }}
                        sx={{
                          marginRight: "10px !important",
                        }}
                      >
                        <Typography
                          variant="button"
                          owner="admin"
                          className="lh"
                        >
                          Cancel
                        </Typography>
                      </Button>
                      <Button
                        variant="contained"
                        owner="admin"
                        dialogbtn="true"
                        dialogvariant="save"
                        className="lh"
                        disabled={selectedSlides.length === 0 || language === '' || isInserting}
                        onClick={async () => {
                          setIsInserting(true);
                          try {
                            await props.onAddTranslatedSlides(
                              selectedSlides,
                              language,
                              processNotes(),
                              processAudio(),
                            );
                            setSelectedPresentation(null);
                            setSelectedSlides([]);
                          } catch (error) {
                            console.error("Error inserting translated slides:", error);
                            setError(true);
                          } finally {
                            setIsInserting(false);
                          }
                        }}
                      >
                        {isInserting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <Typography
                            variant="button"
                            owner="admin"
                            className="lh"
                          >
                            Insert
                          </Typography>
                        )}
                      </Button>
                    </Stack>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                onClose();
              }}
              aria-label="close"
            >
              <Close />
            </IconButton> */}
          </Toolbar>
        </DialogContent>
      </AppBar>

      <DialogContent sx={{ textAlign: isLoading ? "center" : "" }}>
        <>
          <Container fluid className="add-library-container">
            <PresentationFilters
              selector
              pageTitle="Select presentation"
              advanced={true}
              categories={props.categories}
              languages={props.languages}
              themes={props.themes}
              hideStatus={true}
              isAddLibrary={true}
              filters={filters}
              onApply={(filters) => {
                setFilters(filters);
                setCurrentPage(1);
                setLoading(true);
                setSelectedPresentation(null);
              }}
              activeTabIndex={activeIndex}
              getFavourites={() => {
                setActiveIndex(activeIndex === 0 ? 1 : 0);
                setLoading(true);
              }}
            />
            {isError ? (
              <Typography variant="body">
                An error occurred. Please try again.
              </Typography>
            ) : (
              <>
                {isLoading ? (
                  <div className="circular-container fs t">
                    {/* <CircularProgress color='primary' /> */}
                    <CompanyappLoader />
                  </div>
                ) : (
                  <>
                    {overlayLoader && (
                      <div
                        className="circular-container fs t"
                        style={{ backgroundColor: "rgba(255, 255, 255, 0.75)" }}
                      >
                        <CircularProgress size={22} />
                      </div>
                    )}
                    <Row className="mt-4" style={{ position: "relative" }}>
                      {presentations.length > 0 ? (
                        <>
                          {isPaginating && (
                            <Box
                              className="w-100 h-100"
                              sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                                zIndex: 5,
                                background: "rgba(255, 255, 255, 1)",
                              }}
                            >
                              <CompanyappLoader />
                            </Box>
                          )}
                          {presentations.map((presentation) => {
                            if (props.type === "copy") {
                              if (presentation.id === props.presentation.id) {
                                return null;
                              }
                            }

                            return (
                              <Presentation
                                isLibrary={true}
                                key={`presentation-${presentation.id}`}
                                user={props.user}
                                item={presentation}
                                selector
                                onSelected={() => {
                                  setSelectedPresentationId(presentation.id);
                                }}
                                isSelected={
                                  selectedPresentationId === presentation.id
                                    ? true
                                    : false
                                }
                              />
                            );
                          })}
                        </>
                      ) : (
                        <NoData activeIndex={0} />
                      )}
                    </Row>
                    <Row className="mt-1 mb-5">
                      <Col className="d-flex justify-content-center">
                        <Pagination
                          owner="admin"
                          containerClasses={`creator-pagination d-flex justify-content-center align-items-center`}
                          previousDisabled={currentPage === 1 ? true : false}
                          nextDisabled={
                            presentations.length < pageSize ? true : false
                          }
                          currentPage={currentPage}
                          onPrevious={() => {
                            setPaging(true);
                            const current = currentPage - 1;
                            setCurrentPage(current);
                          }}
                          onNext={() => {
                            setPaging(true);
                            const current = currentPage + 1;
                            setCurrentPage(current);
                          }}
                        />
                      </Col>
                    </Row>
                    {selectedPresentation && (
                      <Box
                        sx={{
                          height: "50%",
                          width: 1,
                          // backgroundColor: "primary.light",
                          padding: "0 0.75rem",
                        }}
                      >
                        <Row className="mt-2 pt-4">
                          <Col>
                            <h5>{`Select the LANGUAGE to TRANSLATE to`}</h5>
                          </Col>
                        </Row>

                        <Row className="mt-2 pb-4">
                          <Col>
                            <TextField
                              id="language"
                              label="Language"
                              className="title-textfield"
                              variant="standard"
                              value={language}
                              size="small"
                              placeholder="Enter language or key. Ie: 'French' or 'fr'"
                              onChange={(event) => {
                                // handleTitle(event);
                                setLanguage(event.target.value);
                              }}
                              InputLabelProps={{
                                style: { fontSize: "14px" },
                              }}
                              InputProps={{
                                style: { fontSize: "14px" },
                              }}
                              sx={{ minWidth: "200px" }}
                            />
                          </Col>
                        </Row>
                      </Box>
                    )}
                    {selectedPresentation && (
                      <Box
                        sx={{
                          height: "50%",
                          width: 1,
                          backgroundColor: "primary.light",
                          padding: "0 0.75rem",
                        }}
                      >
                        <Row className="mt-2 pt-4">
                          {selectedPresentation && (
                            <>
                              <Col>
                                <h5>
                                  {`Select slides to DUPLICATE & TRANSLATE from ${selectedPresentation.title} `}
                                  <Typography
                                    variant="span"
                                    color="primary"
                                    className="presentation-info"
                                  >{`${selectedPresentation.title}  (#${selectedPresentation.id})`}</Typography>
                                </h5>
                              </Col>
                              <Col xs={"auto"}>
                                <Stack
                                  direction={"row"}
                                  spacing={2}
                                  sx={{
                                    alignItems: "flex-end",
                                  }}
                                >
                                  <TextField
                                    id="title"
                                    label="Search Slide"
                                    className="title-textfield"
                                    variant="standard"
                                    value={searchString}
                                    size="small"
                                    placeholder="Search Slide"
                                    onChange={(event) => {
                                      // handleTitle(event);
                                      setSearchString(event.target.value);
                                    }}
                                    InputLabelProps={{
                                      style: { fontSize: "14px" },
                                    }}
                                    InputProps={{
                                      style: { fontSize: "14px" },
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          {searchString.length > 0 ? (
                                            <IconButton
                                              className="title-clear-btn"
                                              sx={{
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: 2,
                                              }}
                                              onClick={() => {
                                                setSearchString("");
                                              }}
                                            >
                                              <ClearIcon
                                                sx={{ fontSize: "20px" }}
                                                className="title-adornment-icon"
                                              />
                                            </IconButton>
                                          ) : (
                                            <SearchOutlined
                                              sx={{ fontSize: "20px" }}
                                              className="title-adornment-icon search"
                                            />
                                          )}
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{ minWidth: "200px" }}
                                  />
                                  <Button
                                    variant="outlined"
                                    className="library-btn slides clear"
                                    onClick={() => {
                                      setSelectedSlides([]);
                                    }}
                                    disabled={selectedSlides.length === 0}
                                  >
                                    Clear All
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    className="library-btn slides"
                                    onClick={() => {
                                      selectAllSlides();
                                    }}
                                  >
                                    Select All
                                  </Button>
                                </Stack>
                              </Col>
                            </>
                          )}
                        </Row>
                        <Row ref={slidesContainer} className="mt-4">
                          {selectedPresentation?.slides && (
                            <>
                              {selectedPresentation?.slides?.map(
                                (slide, index) => {
                                  const doesExist =
                                    props.presentation.slides.some(
                                      (s) =>
                                        parseInt(s.id, 10) ===
                                        parseInt(slide.id, 10),
                                    );

                                  const isSelected = selectedSlides.includes(
                                    slide.id,
                                  );

                                  if (!slide.published) {
                                    return null;
                                  }

                                  let isHighlighted = false;
                                  if (searchString.length > 0) {
                                    isHighlighted = checkStringMatch(
                                      searchString,
                                      slide,
                                    );
                                  } else {
                                    isHighlighted = undefined;
                                  }

                                  return (
                                    <SlideThumbnail
                                      isAddLibrary
                                      key={`slide-${slide.id}-${index}`}
                                      user={props.user}
                                      presentation={selectedPresentation}
                                      slide={slide}
                                      selector
                                      preventDuplicate={
                                        props.type !== "copy"
                                          ? slide.prevent_duplicate
                                          : false
                                      }
                                      onSelected={() => onSlideToggle(slide)}
                                      isSelected={isSelected}
                                      isDisabled={
                                        props.type === "copy"
                                          ? doesExist
                                          : false
                                      }
                                      isHighlighted={isHighlighted}
                                      searchString={searchString}
                                      showNumber={index}
                                    />
                                  );
                                },
                              )}
                            </>
                          )}
                        </Row>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Container>
        </>
      </DialogContent>

      <DialogActions></DialogActions>
    </Dialog>
  );
}
